import React, { useState } from 'react';
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  UserX,
  Mail,
  Crown,
  Shield,
  User,
  AlertCircle
} from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';
import {
  getAccountTeamMembers,
  getSubscriptionInfo,
  removeAccountTeamMember,
  getPendingInvitations,
  type AccountTeamMember,
  type SubscriptionInfo,
  type TeamInvitation,
  cancelInvitation
} from '@/lib/accountTeamService';
import { createTeamInvitation } from '@/lib/emailInvitations';
import { ACCOUNT_LEVEL_TEAM_ID } from '@/utils/constants.ts';

export function AccountTeamManagement() {
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [teamMembers, setTeamMembers] = useState<AccountTeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load initial data
  React.useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;
      setInitialLoading(true);
      const { data: subData } = await getSubscriptionInfo(user.id);
      if (subData) setSubscription(subData);
      const { data: membersData } = await getAccountTeamMembers(user.id);
      if (membersData) {
        const filteredMembers = membersData.filter(m => m.id !== user.id);
        const members = [
          {
            id: user.id,
            email: user.email || "admin@example.com",
            name: user.user_metadata?.full_name || "Current User",
            role: "admin" as const,
            status: "active" as const,
            joinedAt: user.created_at,
            lastActive: user.last_sign_in_at
          },
          ...filteredMembers
        ];
        setTeamMembers(members);
      }
      const { data: invitationsData } = await getPendingInvitations(user.id);
      if (invitationsData) setPendingInvitations(invitationsData);
      setInitialLoading(false);
    };
    loadData();
  }, [user?.id]);

  const handleInviteMember = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!subscription) {
      toast.error("Subscription information not available");
      return;
    }
    if (teamMembers.length >= subscription.maxMembers) {
      toast.error(`Your ${subscription.plan} plan allows up to ${subscription.maxMembers} team members. Upgrade to add more.`);
      return;
    }
    if (!user?.id) {
      toast.error("User not authenticated");
      return;
    }
    setLoading(true);
    try {
      const { data: invitation, error } = await createTeamInvitation(
        ACCOUNT_LEVEL_TEAM_ID,
        inviteEmail.toLowerCase(),
        'member',
        user.id,
        'account'
      );
      if (error) throw error;
      if (invitation) {
        setPendingInvitations([...pendingInvitations, invitation]);
        setInviteEmail('');
        setInviteOpen(false);
        toast.success(`Invitation sent to ${inviteEmail}`);
      }
    } catch (error) {
      toast.error(error.message || "Failed to send invitation");
      console.error('Invitation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (member) => {
    try {
      if ('status' in member && member.status === 'pending') {
        const { error } = await cancelInvitation(member.id);
        if (!error) {
          setPendingInvitations(prev => prev.filter(inv => inv.id !== member.id));
          toast.success("Invitation cancelled");
        }
        return;
      }
      const { error } = await removeAccountTeamMember(member.id, user.id);
      if (error) throw error;
      setTeamMembers(teamMembers.filter(m => m.id !== member.id));
      toast.success("Team member removed");
    } catch (error) {
      toast.error(error.message || "Failed to remove team member");
      console.error('Error removing team member:', error);
      return;
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Crown className="h-4 w-4" style={{ color: "#F59E0B" }} />;
      case 'member': return <User className="h-4 w-4" style={{ color: "#3B82F6" }} />;
      default: return <Shield className="h-4 w-4" style={{ color: "#8080A0" }} />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
      active: { color: "#22C55E", background: "color-mix(in srgb, #22C55E 14%, transparent)" },
      pending: { color: "#F59E0B", background: "color-mix(in srgb, #F59E0B 14%, transparent)" },
      suspended: { color: "#EF4444", background: "color-mix(in srgb, #EF4444 14%, transparent)" },
    };
    const label = status === 'active' ? 'Active' : status === 'pending' ? 'Pending' : status === 'suspended' ? 'Suspended' : status;
    return <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold" style={styles[status] || { color: "#8080A0", background: "rgba(255,255,255,.05)" }}>{label}</span>;
  };

  const allMembers = React.useMemo(() => {
    const activeMembers = teamMembers.map(member => ({ ...member, type: 'member' as const }));
    const pendingMembers = pendingInvitations.map(invitation => ({
      id: invitation.id,
      email: invitation.email,
      name: invitation.email.split('@')[0],
      role: invitation.role,
      status: 'pending' as const,
      joinedAt: invitation.invited_at,
      lastActive: 'Never',
      type: 'invitation' as const
    }));
    return [...activeMembers, ...pendingMembers];
  }, [teamMembers, pendingInvitations]);

  const totalMembers = teamMembers.length + pendingInvitations.length;
  const remainingSlots = subscription ? subscription.maxMembers - totalMembers : 0;

  return (
    <div className="space-y-5">
      {/* Team Overview */}
      <div className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5 font-heading text-[20px] text-og01-text">
              <Users className="h-[18px] w-[18px]" style={{ color: "var(--accent, #8B5CF6)" }} />Team Management
            </div>
            <div className="mt-1 text-[13px] text-og01-text-secondary">Manage who has access to your OG01 account</div>
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={() => { alert('Button clicked! Check console for logs.'); }}
              className="rounded-[10px] border border-white/[0.07] bg-og01-card px-3 py-2 text-[12.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
              🔥 Test Console
            </button>
            <button disabled={remainingSlots <= 0} onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-2 rounded-[10px] border-none px-4 py-2 text-[13.5px] font-semibold text-white transition-all hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
              <UserPlus className="h-4 w-4" />Invite Member
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { v: totalMembers, l: "Active Members" },
            { v: remainingSlots, l: "Available Slots" },
            { v: subscription?.maxMembers || 0, l: "Total Allowed" },
          ].map((s) => (
            <div key={s.l} className="rounded-[13px] border border-white/[0.07] bg-[#0F0F18] p-[22px] text-center">
              <div className="mb-1.5 font-heading text-[34px] leading-none text-og01-text">{s.v}</div>
              <div className="text-[12.5px] text-og01-text-secondary">{s.l}</div>
            </div>
          ))}
        </div>

        {remainingSlots <= 0 && (
          <div className="mt-4 rounded-[11px] p-3" style={{ border: "1px solid color-mix(in srgb, #F59E0B 30%, transparent)", background: "color-mix(in srgb, #F59E0B 12%, transparent)" }}>
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4" style={{ color: "#F59E0B" }} />
              <div className="text-sm">
                <p className="font-medium" style={{ color: "#F59E0B" }}>Team limit reached</p>
                <p className="text-og01-text-secondary">Upgrade your plan to add more team members.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Team Members Table */}
      <div className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
        <div className="mb-5">
          <div className="font-heading text-[20px] text-og01-text">Team Members</div>
          <div className="mt-1 text-[13px] text-og01-text-secondary">People who have access to your OG01 account</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr>
                {["Member", "Role", "Status", "Joined", "Last Active"].map((h) => (
                  <th key={h} className="px-4 pb-3 text-left text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">{h}</th>
                ))}
                <th className="px-4 pb-3 text-right text-[11px] font-medium uppercase tracking-[0.05em] text-og01-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allMembers.map((member) => (
                <tr key={member.id} className="hover:bg-white/[0.018]">
                  <td className="border-t border-white/[0.07] px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-full text-[13px] font-semibold text-white" style={{ background: "linear-gradient(135deg,#2a2440,#3a2a52)" }}>
                        {member.name[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-og01-text">{member.name}</div>
                        <div className="text-[12.5px] text-og01-text-muted">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="border-t border-white/[0.07] px-4 py-4">
                    <div className="flex items-center gap-2 text-og01-text-secondary">
                      {getRoleIcon(member.role)}
                      <span className="capitalize">{member.role}</span>
                      {member.email === user?.email && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px]" style={{ color: "#8080A0", background: "rgba(255,255,255,.06)" }}>You</span>
                      )}
                    </div>
                  </td>
                  <td className="border-t border-white/[0.07] px-4 py-4">{getStatusBadge(member.status)}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-og01-text-secondary">{new Date(member.joinedAt).toLocaleDateString()}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-og01-text-secondary">{member.lastActive === "Never" ? "Never" : new Date(member.lastActive).toLocaleDateString()}</td>
                  <td className="border-t border-white/[0.07] px-4 py-4 text-right">
                    {member.email !== user?.email && (
                      <button onClick={() => handleRemoveMember(member.id, member.email)}
                        className="inline-grid h-[30px] w-[30px] place-items-center rounded-lg border border-white/[0.07] bg-[#0F0F18] text-og01-text-secondary transition-colors hover:border-red-500/40 hover:text-[#EF4444]">
                        <UserX className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Member Modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" onClick={() => setInviteOpen(false)}>
          <div className="absolute inset-0" style={{ background: "rgba(4,4,10,0.72)", backdropFilter: "blur(5px)" }} />
          <div className="relative w-full max-w-md rounded-[18px] border border-white/[0.07] bg-og01-card p-7" onClick={e => e.stopPropagation()} style={{ boxShadow: "0 30px 80px -30px rgba(0,0,0,.7)" }}>
            <h3 className="font-heading text-[22px] text-og01-text">Invite Team Member</h3>
            <p className="mb-5 mt-1 text-[13px] text-og01-text-secondary">Send an invitation to join your OG01 account. They'll receive an email to create their account.</p>
            <div className="space-y-4">
              <div>
                <label htmlFor="invite-email" className="mb-1.5 block text-[13px] text-og01-text-secondary">Email Address</label>
                <input id="invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com"
                  className="w-full rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-2.5 text-sm text-og01-text outline-none transition-colors placeholder:text-og01-text-muted focus:border-[#8B5CF6]/60" />
              </div>
              <div className="rounded-[11px] border border-white/[0.07] bg-[#0F0F18] p-3">
                <div className="flex items-start gap-2">
                  <Mail className="mt-0.5 h-4 w-4 text-og01-text-muted" />
                  <div className="text-sm text-og01-text-secondary">
                    <p className="font-medium text-og01-text">What happens next:</p>
                    <ul className="mt-1 space-y-1 text-xs">
                      <li>• They'll receive an email invitation</li>
                      <li>• They can create their own OG01 account</li>
                      <li>• They'll get {subscription?.plan || 'Premium'} level access</li>
                      <li>• You can manage their access anytime</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2.5">
              <button onClick={() => setInviteOpen(false)}
                className="rounded-[10px] border border-white/[0.07] bg-og01-card px-4 py-2.5 text-sm font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
                Cancel
              </button>
              <button onClick={() => handleInviteMember()} disabled={loading || !inviteEmail}
                className="rounded-[10px] border-none px-4 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
                {loading ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
